<template>
  <v-container>
    <v-row class="align-center">
      <v-col cols="auto"><h3>Thumbs</h3></v-col>
      <v-col v-if="showCopyFromEnglish" class="text-right">
        <v-btn
          color="primary"
          :loading="copying"
          :disabled="isAnyBusy"
          data-testid="meta-template-copy-thumbs-from-english"
          @click="copyThumbsFromEnglish"
        >
          Copy thumbs from English
        </v-btn>
      </v-col>
    </v-row>
    <v-row>
      <v-col class="d-flex" cols="12" sm="4" v-for="model in models" :key="model.key">
        <v-card style="width: 100%">
          <v-card-title class="d-flex align-center justify-space-between">
            <span>{{ model.key }} - {{ sizes[model.key] }}</span>
            <v-progress-circular
              v-if="isBusy(model)"
              indeterminate
              color="primary"
              size="20"
              width="2"
            />
          </v-card-title>
          <v-card-text>
            <img v-if="model.path" :src="getUrl(model.path)" style="width: 100%;" alt="thumb" />
          </v-card-text>
          <v-card-actions class="d-flex">
            <v-text-field
              :label="model.key"
              v-model="model.path"
              disabled
            />
            <v-file-input
              :label="getLabel(model.key)"
              :data-testid="`meta-template-thumb-upload-${model.key}`"
              accept="image/*"
              hide-input
              :disabled="isBusy(model)"
              @change="(file) => uploadFor(model, file)"
            />
            <input
              type="file"
              accept="image/*"
              style="display: none;"
              :data-testid="`meta-template-thumb-input-${model.key}`"
              :disabled="isBusy(model)"
              @change="(event) => uploadFor(model, event?.target?.files?.[0])"
            />
            <v-icon
              class="mt-4"
              :data-testid="`meta-template-thumb-delete-${model.key}`"
              :disabled="isBusy(model)"
              @click="() => deleteThumb(model)"
            >
              mdi-trash-can
            </v-icon>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { ClearCacheService } from '@/services/ClearCacheService';

export default {
  name: 'MetaTemplateThumbs',
  props: {
    metaTemplateId: {},
    metaId: {},
    locale: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      sizes: {
        promoThumbPath: '1080x400',
        thumbPath: '600x600',
        secondaryThumbPath: '600x277',
      },
      models: [],
      keys: [],
      uploading: {},
      copying: false,
    };
  },
  computed: {
    showCopyFromEnglish() {
      return this.locale && this.locale !== 'en' && this.metaId;
    },
    isAnyBusy() {
      return this.copying || Object.values(this.uploading).some(Boolean);
    },
  },
  watch: {
    metaId: {
      handler() {
        this.loadThumbs();
      },
    },
  },
  async created() {
    this.keys = await this.$gameApi.get('/dictionary/thumbs');
    await this.loadThumbs();
  },
  methods: {
    isBusy(model) {
      if (!model || !model.key) return false;
      return !!this.uploading[model.key];
    },
    async loadThumbs() {
      if (!this.metaTemplateId || !this.metaId) {
        this.models = this.keys.map((key) => ({ key, path: '' }));
        return;
      }

      const thumbs = await this.$gameApi.get(`/metaTemplates/${this.metaTemplateId}/metas/${this.metaId}/thumbs`);
      this.models = this.keys.map((key) => {
        const existing = thumbs?.find(t => t.key === key);
        return existing || { key, path: '', cacheBust: 0 };
      });
    },
    async uploadFor(model, file) {
      if (!file) return;
      if (!model || !model.key) return;
      this.$set(this.uploading, model.key, true);
      const formData = new FormData();
      formData.set('file', file);
      try {
        const created = await this.$gameApi.post(
          `/metaTemplates/${this.metaTemplateId}/metas/${this.metaId}/thumbs/${model.key}`,
          formData
        );
        model.id = created?.id || model.id;
        const cacheItem = created?.path || created?.url;
        if (cacheItem) {
          try {
            await ClearCacheService.clearCacheByItems([cacheItem], 'Url', 'Delete');
          } catch (e) {
            // non-blocking: log error, but don't break UX
            // eslint-disable-next-line no-console
            console.error('Failed to clear CDN cache for thumb', e);
          }
        }

        const newPath = created?.path || created?.url || model.path;
        if (newPath) {
          model.path = newPath;
          this.$set(model, 'cacheBust', Date.now());
        }
      } finally {
        this.$set(this.uploading, model.key, false);
      }
    },
    getLabel(key) {
      const size = this.sizes[key];
      return key + (size ? ` (${size})` : '');
    },
    getUrl(path) {
      const base = path?.startsWith('http') ? path : path;
      if (!base) return '';
      const bust = this.models.find(m => m.path === path)?.cacheBust;
      return bust ? `${base}?ark__v=${bust}` : base;
    },
    async copyThumbsFromEnglish() {
      const { isConfirmed } = await this.$swal.fire({
        icon: 'question',
        title: 'Copy thumbs from English?',
        text: 'This will replace all thumbnails for this locale with copies from English.',
        showCancelButton: true,
      });

      if (!isConfirmed) return;

      this.copying = true;
      try {
        await this.$gameApi.post(
          `/metaTemplates/${this.metaTemplateId}/thumbs/copy?sourceLocale=en&targetLocale=${this.locale}`
        );
        await this.loadThumbs();
        this.$swal.fire({
          icon: 'success',
          title: 'Thumbnails copied from English.',
          timer: 1500,
          showConfirmButton: false,
        });
      } catch (error) {
        console.error('Error copying thumbs from English:', error);
        this.$swal.fire({
          icon: 'error',
          title: 'Failed to copy thumbnails',
          text: error?.message || 'Unable to copy thumbnails from English.',
        });
      } finally {
        this.copying = false;
      }
    },
    async deleteThumb(model) {
      if (!model?.id) {
        model.path = '';
        return;
      }
      const { isConfirmed } = await this.$swal.fire({
        icon: 'warning',
        title: 'Delete thumbnail?',
        text: 'This will remove the thumbnail.',
        showCancelButton: true,
      });

      if (!isConfirmed) return;

      await this.$gameApi.delete(`/metaTemplates/${this.metaTemplateId}/metas/${this.metaId}/thumbs/${model.id}`);
      model.path = '';
      model.id = undefined;
    }
  },
};
</script>
